import React from "react";

const FALLBACK_KEYS = {
  open: [
    { id: "B", label: "Nearer · lower score", tone: "muted" },
    { id: "A", label: "Farther · higher score · first", tone: "green" },
  ],
  single_area: [
    { id: "A", label: "Inside catchment", tone: "green" },
    { id: "B", label: "Outside", tone: "muted" },
  ],
  inner_outer: [
    { id: "A", label: "Inner / first band", tone: "green" },
    { id: "B", label: "Outer / next band", tone: "blue" },
    { id: "C", label: "Outside", tone: "muted" },
  ],
  two_bands: [
    { id: "A", label: "First priority band", tone: "green" },
    { id: "B", label: "Second priority band", tone: "blue" },
    { id: "C", label: "Outside", tone: "muted" },
  ],
  priority_layers: [
    { id: "A", label: "Priority band", tone: "amber" },
    { id: "B", label: "Next band", tone: "green" },
    { id: "C", label: "Further / outside", tone: "blue" },
  ],
};

function Pin({ x, y, label, fill }) {
  return (
    <g>
      <circle cx={x} cy={y} r="10" fill="rgba(255,255,255,0.94)" />
      <circle cx={x} cy={y} r="7.5" fill={fill} />
      <text x={x} y={y + 3.2} textAnchor="middle" className="sj-example-mark">
        {label}
      </text>
    </g>
  );
}

function ExampleSketch({ kind }) {
  if (kind === "open") {
    return (
      <svg viewBox="0 0 248 132" className="sj-example-sketch" role="img" aria-label="Open catchment example">
        <defs>
          <radialGradient id="sj-ex-open-glow" cx="50%" cy="45%" r="55%">
            <stop offset="0%" stopColor="rgba(5,150,105,0.10)" />
            <stop offset="100%" stopColor="rgba(5,150,105,0)" />
          </radialGradient>
        </defs>
        <rect x="1" y="1" width="246" height="130" rx="18" fill="#f8fafc" stroke="#e2e8f0" />
        <circle cx="118" cy="58" r="52" fill="url(#sj-ex-open-glow)" />
        <circle cx="118" cy="58" r="38" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="3.5 5.5" />
        <circle cx="118" cy="58" r="4.5" fill="#00515b" />
        <circle cx="118" cy="58" r="9" fill="none" stroke="#00515b" strokeWidth="1.1" opacity="0.35" />
        <line x1="118" y1="58" x2="78" y2="40" stroke="#cbd5e1" strokeWidth="1.1" strokeDasharray="2.5 3.5" />
        <line x1="118" y1="58" x2="204" y2="96" stroke="#cbd5e1" strokeWidth="1.1" strokeDasharray="2.5 3.5" />
        <Pin x={78} y={40} label="B" fill="#64748b" />
        <Pin x={204} y={96} label="A" fill="#059669" />
      </svg>
    );
  }

  if (kind === "single_area") {
    return (
      <svg viewBox="0 0 248 132" className="sj-example-sketch" role="img" aria-label="Single catchment example">
        <rect x="1" y="1" width="246" height="130" rx="18" fill="#f8fafc" stroke="#e2e8f0" />
        <circle cx="108" cy="62" r="42" fill="#bbf7d0" stroke="#059669" strokeWidth="1.6" />
        <circle cx="108" cy="62" r="4.5" fill="#00515b" />
        <Pin x={100} y={50} label="A" fill="#059669" />
        <Pin x={204} y={36} label="B" fill="#64748b" />
      </svg>
    );
  }

  if (kind === "inner_outer" || kind === "two_bands") {
    return (
      <svg viewBox="0 0 248 132" className="sj-example-sketch" role="img" aria-label="Two catchment bands example">
        <rect x="1" y="1" width="246" height="130" rx="18" fill="#f8fafc" stroke="#e2e8f0" />
        <circle cx="108" cy="60" r="46" fill="#dbeafe" stroke="#3b82f6" strokeWidth="1.5" />
        <circle cx="108" cy="60" r="24" fill="#bbf7d0" stroke="#059669" strokeWidth="1.6" />
        <circle cx="108" cy="60" r="4.5" fill="#00515b" />
        <Pin x={108} y={48} label="A" fill="#059669" />
        <Pin x={138} y={82} label="B" fill="#2563eb" />
        <Pin x={206} y={34} label="C" fill="#64748b" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 248 132" className="sj-example-sketch" role="img" aria-label="Priority bands example">
      <rect x="1" y="1" width="246" height="130" rx="18" fill="#f8fafc" stroke="#e2e8f0" />
      <rect x="22" y="18" width="150" height="84" rx="16" fill="#dbeafe" stroke="#3b82f6" strokeWidth="1.4" />
      <rect x="40" y="32" width="114" height="56" rx="14" fill="#bbf7d0" stroke="#059669" strokeWidth="1.4" />
      <rect x="58" y="44" width="78" height="32" rx="12" fill="#fef3c7" stroke="#d97706" strokeWidth="1.5" />
      <circle cx="97" cy="60" r="4" fill="#00515b" />
      <Pin x={86} y={52} label="A" fill="#d97706" />
      <Pin x={128} y={74} label="B" fill="#059669" />
      <Pin x={204} y={38} label="C" fill="#2563eb" />
    </svg>
  );
}

export default function CatchmentExample({ example }) {
  if (!example?.body) return null;
  const keys = Array.isArray(example.keys) && example.keys.length
    ? example.keys
    : FALLBACK_KEYS[example.kind] || [];
  return (
    <aside className="sj-example" aria-label={example.title || "Example"}>
      <div className="sj-example-copy">
        <p className="sj-example-kicker">{example.title || "Example"}</p>
        <p className="sj-example-body">{example.body}</p>
        {keys.length ? (
          <ul className="sj-example-keys">
            {keys.map((key) => (
              <li key={key.id} className={`sj-example-key sj-example-key--${key.tone}`}>
                <span className="sj-example-key-id">{key.id}</span>
                <span>{key.label}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {example.kind ? <ExampleSketch kind={example.kind} /> : null}
    </aside>
  );
}
